// CancelledMarker.js - Avbokningar som EN liten ikon per kolumn och starttid (Spår S2)
//
// Avbokade bokningar ligger utanför kollisionslayouten och tar ingen bredd
// från aktiva bokningar. Ikonen sitter i kolumnens högerkant med antal (>1),
// hover visar alla avbokningar på tiden och klick fäster listan så att man
// kan öppna respektive bokning. Delas av dagvyn (DayView) och veckovyn (Calendar).

import React, { useState, useEffect, useRef } from 'react';
import './CancelledMarker.css';

const pad = (n) => String(n).padStart(2, '0');
const formatMinutes = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;

// "24 okt 14:12" - när bokningen avbokades (senast ändrad)
const formatCancelledAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const labelFor = (apt) => (apt.is_group
  ? (apt.group_name || 'Gruppbokning')
  : (apt.patient ? apt.patient.fullname : 'Ingen patient'));

// Gruppera avbokade event (med startMinutes/endMinutes) per starttid
export const groupCancelledByStart = (cancelledEvents) => {
  const byStart = new Map();
  cancelledEvents.forEach(ev => {
    if (!byStart.has(ev.startMinutes)) byStart.set(ev.startMinutes, []);
    byStart.get(ev.startMinutes).push(ev);
  });
  return [...byStart.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([startMinutes, items]) => ({
      startMinutes,
      items: items.slice().sort((a, b) => labelFor(a).localeCompare(labelFor(b), 'sv'))
    }));
};

// Ikonen sitter i kolumnens övre högra hörn vid starttiden (CancelledMarker.css:
// right 2px, margin-top 2px, höjd 16px). Kort i kolumnens högerkant som ligger
// under ikonen görs smalare så att krysset syns bredvid kortet i stället för
// ovanpå det (och inte krockar med betald/genomförd-pricken).
const ICON_TOP_PX = 2;
const ICON_HEIGHT_PX = 16;
const GUTTER_SINGLE_PX = 22;   // kryss + luft
const GUTTER_COUNT_PX = 30;    // kryss + antal + luft

export const applyCancelledGutter = (events, cancelledGroups, pixelsPerMinute) => {
  const windowMinutes = (ICON_TOP_PX + ICON_HEIGHT_PX) / pixelsPerMinute;
  events.forEach(event => {
    event.cancelledGutter = 0;
    if (event.column !== event.totalColumns - 1) return;  // bara högerkanten
    cancelledGroups.forEach(group => {
      const overlaps = event.startMinutes < group.startMinutes + windowMinutes
        && event.endMinutes > group.startMinutes;
      if (!overlaps) return;
      const gutter = group.items.length > 1 ? GUTTER_COUNT_PX : GUTTER_SINGLE_PX;
      event.cancelledGutter = Math.max(event.cancelledGutter, gutter);
    });
  });
};

// Listans bredd inkl. marginal (CancelledMarker.css)
const POPOVER_SPACE = 250;

const CancelledMarker = ({ group, top, onOpen, onContextMenu }) => {
  const [pinned, setPinned] = useState(false);
  // Listan öppnar åt vänster; i första kolumnen räcker inte platsen → åt höger
  const [flip, setFlip] = useState(false);
  const ref = useRef(null);

  const updateFlip = () => {
    if (!ref.current) return;
    const gridLeft = ref.current.closest('.dayview-grid, .calendar-grid')?.getBoundingClientRect().left ?? 0;
    setFlip(ref.current.getBoundingClientRect().left - POPOVER_SPACE < gridLeft);
  };
  const { items, startMinutes } = group;
  const count = items.length;
  const time = formatMinutes(startMinutes);

  // Fäst lista stängs vid klick utanför eller Escape. Capture-fas: kalenderns
  // kort stoppar mousedown (drag), och då når händelsen aldrig bubblingsfasen.
  useEffect(() => {
    if (!pinned) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setPinned(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPinned(false); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned]);

  const summary = count === 1 ? `1 avbokning ${time}` : `${count} avbokningar ${time}`;

  return (
    <div
      ref={ref}
      className={`cancelled-stack ${pinned ? 'pinned' : ''} ${flip ? 'flip' : ''}`}
      style={{ top: `${top}px` }}
      onMouseEnter={updateFlip}
    >
      <button
        type="button"
        className="cancelled-stack-icon"
        onClick={() => { updateFlip(); setPinned(p => !p); }}
        // En avbokning: bokningens högerklicksmeny. Flera: fäst listan, där
        // varje rad har sin egen högerklicksmeny.
        onContextMenu={(e) => {
          if (count === 1) { onContextMenu(items[0], e); return; }
          e.preventDefault();
          updateFlip();
          setPinned(true);
        }}
        aria-label={summary}
        aria-expanded={pinned}
      >
        <i className="fas fa-times" aria-hidden="true"></i>
        {count > 1 && <span className="cancelled-stack-count">{count}</span>}
      </button>
      <div className="cancelled-stack-popover" role="dialog" aria-label={summary}>
        <div className="cancelled-stack-heading">
          Avbokat {time}{count > 1 ? ` (${count})` : ''}
        </div>
        <ul className="cancelled-stack-list">
          {items.map(apt => {
            const cancelledAt = formatCancelledAt(apt.updated_at);
            const color = apt.appointment_type?.color || (apt.is_group ? '#6f42c1' : '#3788d8');
            return (
              <li key={apt.appointment_id ?? `${apt.parent_id}-${apt.start_time}`}>
                <button
                  type="button"
                  className="cancelled-stack-item"
                  title="Öppna bokningen"
                  onClick={(e) => { setPinned(false); onOpen(apt, e); }}
                  onContextMenu={(e) => { setPinned(false); onContextMenu(apt, e); }}
                >
                  <span className="cancelled-stack-swatch" style={{ backgroundColor: color }} />
                  <span className="cancelled-stack-text">
                    <strong>{labelFor(apt)}</strong>
                    <span>
                      {formatMinutes(apt.startMinutes)}–{formatMinutes(apt.endMinutes)}
                      {apt.appointment_type && ` · ${apt.appointment_type.name}`}
                    </span>
                    {cancelledAt && <span className="cancelled-stack-when">Avbokad {cancelledAt}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default CancelledMarker;
